import type { ReturnZod } from "../../return";
import {
  IntegrationProviders,
  TrackstarReturnProvider,
} from "../../return-integration-types";

export const BLUEYONDER_INTEGRATION = "BlueYonder";
export const RETURNBEAR_INTEGRATION = "ReturnBear";

export enum ExternalRmaIntegration {
  AIMS360 = "Aims360",
  APPAREL_MAGIC = "ApparelMagic",
  BLUE_CHERRY = "BlueCherry",
  BRIGHTPEARL = "Brightpearl",
  CLOUDX = "CloudX",
  DEPOSCO = "Deposco",
  ECWID = "Ecwid",
  EXTENSIV = "Extensiv",
  FLEXPORT = "Flexport",
  INMAR = "Inmar",
  MASONHUB = "Masonhub",
  MINTSOFT = "Mintsoft",
  NETSUITE = "NetSuite",
  RYDER = "Ryder",
  SFOM = "SFOM",
  SHIPBOB = "ShipBob",
  SHIPEDGE = "Shipedge",
  SHIPHERO = "ShipHero",
  SHIPMONK = "ShipMonk",
  TRACKSTAR = "Trackstar",
}

export const CreateExternalIntegrationIdProvider = {
  ...ExternalRmaIntegration,
  BLUEYONDER: BLUEYONDER_INTEGRATION,
  RETURNBEAR: RETURNBEAR_INTEGRATION,
} as const;
export type CreateExternalIntegrationIdProvider =
  (typeof CreateExternalIntegrationIdProvider)[keyof typeof CreateExternalIntegrationIdProvider];

export enum CreateExternalIntegrationIdType {
  RMA = "RMA",
  SUPPORT_CASE = "SUPPORT_CASE",
  CASH_REFUND = "CASH_REFUND",
  CREDIT_MEMO = "CREDIT_MEMO",
  REFUND = "REFUND",
  SALES_CREDIT = "SALES_CREDIT",
  RECEIPT = "RECEIPT",
  SALES_ORDER = "SALES_ORDER",
  VENDOR_RETURN_AUTHORIZATION = "VENDOR_RETURN_AUTHORIZATION",
  BOX = "BOX",
}

const createProviderAllowedTypes = {
  [CreateExternalIntegrationIdProvider.AIMS360]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.APPAREL_MAGIC]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.CREDIT_MEMO,
  ],
  [CreateExternalIntegrationIdProvider.BLUE_CHERRY]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.BRIGHTPEARL]: [
    CreateExternalIntegrationIdType.SALES_CREDIT,
  ],
  [CreateExternalIntegrationIdProvider.CLOUDX]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.DEPOSCO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.ECWID]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.EXTENSIV]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.FLEXPORT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.INMAR]: [
    CreateExternalIntegrationIdType.BOX,
  ],
  [CreateExternalIntegrationIdProvider.MASONHUB]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.MINTSOFT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.NETSUITE]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.SUPPORT_CASE,
    CreateExternalIntegrationIdType.RECEIPT,
    CreateExternalIntegrationIdType.CASH_REFUND,
    CreateExternalIntegrationIdType.CREDIT_MEMO,
    CreateExternalIntegrationIdType.REFUND,
    CreateExternalIntegrationIdType.SALES_ORDER,
    CreateExternalIntegrationIdType.VENDOR_RETURN_AUTHORIZATION,
  ],
  [CreateExternalIntegrationIdProvider.RETURNBEAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.RYDER]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SFOM]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPBOB]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPEDGE]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPHERO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPMONK]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.TRACKSTAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.BLUEYONDER]: [
    CreateExternalIntegrationIdType.RMA,
  ],
} as const satisfies Record<
  CreateExternalIntegrationIdProvider,
  readonly CreateExternalIntegrationIdType[]
>;

type CreateAllowedTypesFor<P extends CreateExternalIntegrationIdProvider> =
  (typeof createProviderAllowedTypes)[P][number];

export type ValidCreateProviderTypePair = {
  [P in CreateExternalIntegrationIdProvider]: {
    provider: P;
    type: CreateAllowedTypesFor<P>;
  };
}[CreateExternalIntegrationIdProvider];

export type ValidCreateExternalIntegrationId = ValidCreateProviderTypePair & {
  id: string;
  shipmentGroupId?: string | null;
};

export type CreateExternalIntegrationId = NonNullable<
  ReturnZod["createExternalIntegrationIds"]
>[number];

export type FindCreateExternalIntegrationIdParams =
  ValidCreateProviderTypePair & { shipmentGroupId?: string; id?: string };

export function createExternalIntegrationId(
  params: ValidCreateExternalIntegrationId,
): CreateExternalIntegrationId {
  return params;
}

function matchesCreateExternalIntegrationIdParams(
  extId: CreateExternalIntegrationId,
  params: FindCreateExternalIntegrationIdParams,
): boolean {
  if (extId.provider !== params.provider) {
    return false;
  }
  if (extId.type !== params.type) {
    return false;
  }
  if (
    params.shipmentGroupId !== undefined &&
    extId.shipmentGroupId !== params.shipmentGroupId
  ) {
    return false;
  }
  if (params.id !== undefined && extId.id !== params.id) {
    return false;
  }
  return true;
}

export function findCreateExternalIntegrationId(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): CreateExternalIntegrationId | undefined {
  if (!createExternalIntegrationIds) {
    return undefined;
  }
  return createExternalIntegrationIds.find((extId) =>
    matchesCreateExternalIntegrationIdParams(extId, params),
  );
}

export function filterCreateExternalIntegrationIds(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): CreateExternalIntegrationId[] {
  if (!createExternalIntegrationIds) {
    return [];
  }
  return createExternalIntegrationIds.filter((extId) =>
    matchesCreateExternalIntegrationIdParams(extId, params),
  );
}

export function hasCreateExternalIntegrationId(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): boolean {
  return (
    findCreateExternalIntegrationId(createExternalIntegrationIds, params) !==
    undefined
  );
}

const integrationProviderToExternalRma: Record<
  IntegrationProviders,
  ExternalRmaIntegration
> = {
  [IntegrationProviders.AIMS360]: ExternalRmaIntegration.AIMS360,
  [IntegrationProviders.APPAREL_MAGIC]: ExternalRmaIntegration.APPAREL_MAGIC,
  [IntegrationProviders.BLUE_CHERRY]: ExternalRmaIntegration.BLUE_CHERRY,
  [IntegrationProviders.BRIGHTPEARL]: ExternalRmaIntegration.BRIGHTPEARL,
  [IntegrationProviders.CLOUDX]: ExternalRmaIntegration.CLOUDX,
  [IntegrationProviders.DEPOSCO]: ExternalRmaIntegration.DEPOSCO,
  [IntegrationProviders.ECWID]: ExternalRmaIntegration.ECWID,
  [IntegrationProviders.EXTENSIV]: ExternalRmaIntegration.EXTENSIV,
  [IntegrationProviders.FLEXPORT]: ExternalRmaIntegration.FLEXPORT,
  [IntegrationProviders.INMAR]: ExternalRmaIntegration.INMAR,
  [IntegrationProviders.MASONHUB]: ExternalRmaIntegration.MASONHUB,
  [IntegrationProviders.MINTSOFT]: ExternalRmaIntegration.MINTSOFT,
  [IntegrationProviders.NETSUITE]: ExternalRmaIntegration.NETSUITE,
  [IntegrationProviders.RYDER]: ExternalRmaIntegration.RYDER,
  [IntegrationProviders.SFOM]: ExternalRmaIntegration.SFOM,
  [IntegrationProviders.SHIPBOB]: ExternalRmaIntegration.SHIPBOB,
  [IntegrationProviders.SHIPEDGE]: ExternalRmaIntegration.SHIPEDGE,
  [IntegrationProviders.SHIPHERO]: ExternalRmaIntegration.SHIPHERO,
  [IntegrationProviders.SHIPMONK]: ExternalRmaIntegration.SHIPMONK,
  [IntegrationProviders.TRACKSTAR]: ExternalRmaIntegration.TRACKSTAR,
};

/**
 * Map a createRmaResults `name` onto the ExternalRmaIntegration enum used by
 * createExternalRmaResults.provider. Accepts three input shapes:
 *   - lowercase legacy IntegrationProviders values stored in old DB records
 *   - new ExternalRmaIntegration values written by current code
 *   - TrackstarReturnProvider sub-providers, which all collapse to TRACKSTAR
 *
 * IntegrationProviders and ExternalRmaIntegration are checked before
 * TrackstarReturnProvider because "shipmonk" and "deposco" appear in
 * IntegrationProviders, and ExternalRmaIntegration string values are distinct
 * from any TrackstarReturnProvider value.
 */
export function legacyRmaResultNameToExternalRmaIntegration(
  name: IntegrationProviders | TrackstarReturnProvider | ExternalRmaIntegration,
): ExternalRmaIntegration {
  const direct = integrationProviderToExternalRma[name as IntegrationProviders];
  if (direct !== undefined) {
    return direct;
  }
  if (
    Object.values(ExternalRmaIntegration).includes(
      name as ExternalRmaIntegration,
    )
  ) {
    return name as ExternalRmaIntegration;
  }
  return ExternalRmaIntegration.TRACKSTAR;
}

export function isRedoReturnsCenterDestination(return_: {
  merchant_address?: { returnsCenterId?: string | null } | null;
}): boolean {
  return !!return_.merchant_address?.returnsCenterId;
}

// Translates integrationName to its legacy lowercase DB form before comparing so PascalCase ExternalRmaIntegration values match createRmaResults[].name.
export function shouldSkipRmaResultForIntegration(
  createRmaResults:
    | readonly {
        name: IntegrationProviders | TrackstarReturnProvider;
        merchantStopRetries: boolean;
      }[]
    | null
    | undefined,
  integrationName: ExternalRmaIntegration | TrackstarReturnProvider,
): boolean {
  const legacyName =
    externalRmaIntegrationToLegacyRmaResultName(integrationName);
  return (
    createRmaResults?.some(
      (r) => r.name === legacyName && r.merchantStopRetries,
    ) ?? false
  );
}

/**
 * Reverse of integrationProviderToExternalRma. Used at the legacy
 * createRmaResults[].name write boundary so that runtime values stay
 * lowercase even though ReturnIntegration subclasses now construct with
 * ExternalRmaIntegration values. Remove together with the legacy
 * createRmaResults field.
 */
const externalRmaIntegrationToLegacyProvider = {
  [ExternalRmaIntegration.AIMS360]: IntegrationProviders.AIMS360,
  [ExternalRmaIntegration.APPAREL_MAGIC]: IntegrationProviders.APPAREL_MAGIC,
  [ExternalRmaIntegration.BLUE_CHERRY]: IntegrationProviders.BLUE_CHERRY,
  [ExternalRmaIntegration.BRIGHTPEARL]: IntegrationProviders.BRIGHTPEARL,
  [ExternalRmaIntegration.CLOUDX]: IntegrationProviders.CLOUDX,
  [ExternalRmaIntegration.DEPOSCO]: IntegrationProviders.DEPOSCO,
  [ExternalRmaIntegration.ECWID]: IntegrationProviders.ECWID,
  [ExternalRmaIntegration.EXTENSIV]: IntegrationProviders.EXTENSIV,
  [ExternalRmaIntegration.FLEXPORT]: IntegrationProviders.FLEXPORT,
  [ExternalRmaIntegration.INMAR]: IntegrationProviders.INMAR,
  [ExternalRmaIntegration.MASONHUB]: IntegrationProviders.MASONHUB,
  [ExternalRmaIntegration.MINTSOFT]: IntegrationProviders.MINTSOFT,
  [ExternalRmaIntegration.NETSUITE]: IntegrationProviders.NETSUITE,
  [ExternalRmaIntegration.RYDER]: IntegrationProviders.RYDER,
  [ExternalRmaIntegration.SFOM]: IntegrationProviders.SFOM,
  [ExternalRmaIntegration.SHIPBOB]: IntegrationProviders.SHIPBOB,
  [ExternalRmaIntegration.SHIPEDGE]: IntegrationProviders.SHIPEDGE,
  [ExternalRmaIntegration.SHIPHERO]: IntegrationProviders.SHIPHERO,
  [ExternalRmaIntegration.SHIPMONK]: IntegrationProviders.SHIPMONK,
  [ExternalRmaIntegration.TRACKSTAR]: IntegrationProviders.TRACKSTAR,
} as const satisfies Record<ExternalRmaIntegration, IntegrationProviders>;

/**
 * Convert an ExternalRmaIntegration or TrackstarReturnProvider value into the
 * lowercase IntegrationProviders | TrackstarReturnProvider form that the
 * legacy createRmaResults[].name field stores. TrackstarReturnProvider values
 * pass through unchanged (they're already lowercase and not in
 * IntegrationProviders). Used only by the legacy field writer.
 */
export function externalRmaIntegrationToLegacyRmaResultName(
  name: ExternalRmaIntegration | TrackstarReturnProvider,
): IntegrationProviders | TrackstarReturnProvider {
  return (
    externalRmaIntegrationToLegacyProvider[name as ExternalRmaIntegration] ??
    (name as TrackstarReturnProvider)
  );
}
